import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { MarinaSetting, BusinessHours, ReservationRules } from '../../models/types';
import { db } from '../config';

// Collection reference
const settingsRef = collection(db, 'settings');

const SETTINGS_DOC_ID = 'marinaSettings';

// Minimal placeholder instead of detailed default business hours
const placeholderHours: BusinessHours = {
  monday: { openTime: '', closeTime: '' },
  tuesday: { openTime: '', closeTime: '' },
  wednesday: { openTime: '', closeTime: '' },
  thursday: { openTime: '', closeTime: '' },
  friday: { openTime: '', closeTime: '' },
  saturday: { openTime: '', closeTime: '' },
  sunday: { openTime: '', closeTime: '' }
};

const defaultReservationRules: ReservationRules = {
  minReservationDuration: 2,
  maxReservationDuration: 8,
  advanceBookingDays: 30,
  checkInTimeMinutes: 15, // Default 15 minutes before reservation
  cancellationPolicy: '24-hour cancellation policy',
  cancellationDeadlineHours: 24, // Default to 24 hours before reservation
  refundPolicy: 'Refunds will be processed according to our cancellation policy.',
  damageWaiverPolicy: 'By accepting our damage waiver, you limit your financial responsibility for accidental damage.',
  depositRequired: true,
  depositType: 'fixed',
  depositAmount: 50,
  baseRate: 100,
  salesTaxRate: 0 // Default to 0% (no tax)
};

// Initialize marina settings with default values
const initializeMarinaSettings = async (): Promise<MarinaSetting> => {
  const defaultSettings: MarinaSetting = {
    id: SETTINGS_DOC_ID,
    businessName: 'Fallen Leaf Marina',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    websiteUrl: '',
    businessHours: placeholderHours,
    inSeasonHours: placeholderHours,
    outOfSeasonHours: placeholderHours,
    seasonStartDate: '05-01',
    seasonEndDate: '09-30',
    isSeasonalHoursEnabled: false,
    reservationRules: defaultReservationRules,
    marinaImageBackground: '',
    // Payment processors configuration
    activePaymentMethods: {
      square: false
    },
    // Square API credentials (empty by default)
    squareApplicationId: '',
    squareLocationId: '',
    squareAccessToken: '',
    squareEnvironment: 'sandbox', // Default to sandbox environment
    updatedAt: new Date()
  };

  await setDoc(doc(db, 'settings', SETTINGS_DOC_ID), defaultSettings);
  return defaultSettings;
};

// Ensure all required fields exist in marina settings
const ensureRequiredFields = async (settings: MarinaSetting): Promise<MarinaSetting> => {
  const updatedSettings = {
    ...settings,
    businessHours: settings.businessHours || placeholderHours,
    inSeasonHours: settings.inSeasonHours || placeholderHours,
    outOfSeasonHours: settings.outOfSeasonHours || placeholderHours,
    reservationRules: settings.reservationRules || defaultReservationRules,
    isSeasonalHoursEnabled: settings.isSeasonalHoursEnabled ?? false,
    seasonStartDate: settings.seasonStartDate || '05-01',
    seasonEndDate: settings.seasonEndDate || '09-30',
    marinaImageBackground: settings.marinaImageBackground || '',
    updatedAt: new Date()
  };

  // Update the document with any missing fields
  await updateMarinaSettings(updatedSettings);
  return updatedSettings;
};

// Get marina settings
export const getMarinaSettings = async (): Promise<MarinaSetting> => {
  const docRef = doc(db, 'settings', SETTINGS_DOC_ID);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as MarinaSetting;
  }

  throw new Error('Marina settings not found. Please initialize settings in the admin panel.');
};

// Get policies from the separate policies document
export const getPolicies = async (): Promise<any> => {
  const policiesRef = doc(db, 'settings', 'policies');
  const docSnap = await getDoc(policiesRef);

  if (docSnap.exists()) {
    return docSnap.data();
  }

  return null;
};

// Update marina settings
export const updateMarinaSettings = async (settings: Partial<MarinaSetting>): Promise<void> => {
  const docRef = doc(db, 'settings', SETTINGS_DOC_ID);
  
  console.log('💾 Updating marina settings with:', settings);
  console.log('🖼️ Image background:', settings.marinaImageBackground);
  
  try {
    await updateDoc(docRef, {
      ...settings,
      updatedAt: new Date()
    });
    console.log('✅ Marina settings updated successfully');
    
    // Verify the update by reading back the data
    const updatedDoc = await getDoc(docRef);
    if (updatedDoc.exists()) {
      const data = updatedDoc.data() as MarinaSetting;
      console.log('🔍 Verified updated settings:', data);
      console.log('🖼️ Verified image background:', data.marinaImageBackground);
    }
  } catch (error) {
    console.error('❌ Error updating marina settings:', error);
    throw error;
  }
};

// Update business hours
export const updateBusinessHours = async (hours: BusinessHours): Promise<void> => {
  await updateMarinaSettings({ businessHours: hours });
};

// Update in-season business hours
export const updateInSeasonBusinessHours = async (hours: BusinessHours): Promise<void> => {
  await updateMarinaSettings({ inSeasonHours: hours });
};

// Update out-of-season business hours
export const updateOutOfSeasonBusinessHours = async (hours: BusinessHours): Promise<void> => {
  await updateMarinaSettings({ outOfSeasonHours: hours });
};

// Update season dates
export const updateSeasonDates = async (
  startDate: string,
  endDate: string,
  isEnabled: boolean
): Promise<void> => {
  await updateMarinaSettings({
    seasonStartDate: startDate,
    seasonEndDate: endDate,
    isSeasonalHoursEnabled: isEnabled
  });
};

// Update reservation rules
export const updateReservationRules = async (rules: ReservationRules): Promise<void> => {
  await updateMarinaSettings({ reservationRules: rules });
}; 